import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Overview</h2>
        <p className="mb-4">
          <strong>moujib.com</strong> is a SaaS platform for comment moderation on Facebook Pages and
          Instagram professional accounts. We help you delete spam, hide comments, reply publicly or via
          DM, and automate actions with rule‑based moderation. We are committed to protecting your privacy
          and handling your data responsibly.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Data We Process</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li>Account data: name, email, authentication identifiers.</li>
          <li>Facebook profile, Page, and linked Instagram professional account metadata you connect (IDs, names, roles, tokens).</li>
          <li>Comments and related content processed for moderation and replies.</li>
          <li>Rule configurations, prompts/instructions, and action logs.</li>
          <li>Usage and diagnostic data (device, events, error logs) for reliability and security.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">Sources of Data</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li>Information you provide directly in the app.</li>
          <li>Meta Graph APIs (Facebook and Instagram) for the Pages and Instagram accounts you authorize.</li>
          <li>Service providers (hosting, analytics) that support our operations.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">Facebook & Instagram Permissions & Tokens</h2>
        <p className="mb-4">
          When you connect your Facebook profile, we request the minimum permissions to discover and
          verify the Pages you administer, along with any Instagram professional (Business or Creator)
          accounts linked to those Pages. When you connect a specific Page or Instagram account, we
          request additional permissions necessary to read and manage comments, hide or delete comments,
          reply publicly, and send private replies (DM) where supported by the platform, as configured.
          Access tokens are stored securely and used only to perform authorized actions. You may revoke
          permissions via your Facebook or Instagram settings at any time; this may disable related
          features.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">How We Use Data</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li>Provide features like moderation, replies/DMs, and rule‑based automations.</li>
          <li>Operate, secure, and improve the Service, including monitoring and troubleshooting.</li>
          <li>Comply with legal obligations and platform policies (e.g., the Meta Platform Terms and the Facebook and Instagram Platform Policies).</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">AI Processing</h2>
        <p className="mb-4">
          If enabled, AI providers may process text you submit (comments, prompts, rules) to generate
          responses or classifications. We use data minimization and avoid sending unnecessary personal
          data. You remain responsible for reviewing outputs before sending.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Sharing with Service Providers</h2>
        <p className="mb-4">
          We may share necessary data with vetted processors (cloud hosting, databases, logging,
          email, AI providers) under contractual safeguards. We do not sell your personal data.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Retention</h2>
        <p className="mb-4">
          We retain operational data only as long as needed to provide the Service or as required by
          law. You may request deletion; some records may be retained for security, audit, or legal
          obligations.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Security</h2>
        <p className="mb-4">
          We use HTTPS, access controls, and role‑based permissions. Admin tools are restricted to
          authorized staff. No method of transmission or storage is 100% secure, but we take
          reasonable measures to protect your data.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Your Rights & Controls</h2>
        <ul className="list-disc ml-6 space-y-2">
          <li>Access, correct, or delete certain data, subject to legal exceptions.</li>
          <li>Disconnect Facebook and Instagram integrations and revoke permissions via your Facebook or Instagram settings.</li>
          <li>Object to or restrict certain processing where applicable by law.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-2">Meta Data Deletion (Facebook & Instagram)</h2>
        <p className="mb-4">
          If you revoke your permissions or delete your Facebook or Instagram account, we will automatically
          receive a data deletion request from Meta. Upon receiving this request, we will permanently
          delete all data associated with your Meta account, including:
        </p>
        <ul className="list-disc ml-6 space-y-2 mb-4">
          <li>Your user account and profile information</li>
          <li>All connected Facebook Pages, linked Instagram accounts, and their data</li>
          <li>Comments, posts, and messages associated with your account</li>
          <li>Moderation rules and configurations</li>
          <li>Analytics and logs (marked for deletion within 30 days)</li>
        </ul>
        <p className="mb-4">
          You will receive a confirmation code that you can use to check the status of your data deletion 
          request at any time. The deletion process typically completes within a few minutes. For questions 
          about data deletion, please contact us at khalil@moujib.com.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-2">Contact</h2>
        <p className="mb-4">Questions or requests: khalil@moujib.com.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 